import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_end?: string;
}

interface SubscriptionContextType {
  subscription: SubscriptionData;
  isLoading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: (plan: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  clearCache: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionData>({ subscribed: false });
  const [isLoading, setIsLoading] = useState(true);
  
  // Use refs instead of state for cache to avoid re-render loops
  const cacheTimestampRef = useRef(0);
  const subscriptionRef = useRef<SubscriptionData>({ subscribed: false });
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const isCheckingRef = useRef(false);
  const failureCountRef = useRef(0);
  const lastFailureTimeRef = useRef(0);
  const hasInitializedRef = useRef(false);

  const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
  const DEBOUNCE_DELAY = 2000;
  const CIRCUIT_BREAKER_THRESHOLD = 3;
  const CIRCUIT_BREAKER_RESET = 5 * 60 * 1000;

  // Keep ref in sync with state
  const updateSubscription = useCallback((data: SubscriptionData) => {
    subscriptionRef.current = data;
    setSubscription(data);
  }, []);

  const checkSubscription = useCallback(async () => {
    const now = Date.now();
    if (failureCountRef.current >= CIRCUIT_BREAKER_THRESHOLD) {
      if (now - lastFailureTimeRef.current < CIRCUIT_BREAKER_RESET) {
        setIsLoading(false);
        return;
      } else {
        failureCountRef.current = 0;
      }
    }

    if (isCheckingRef.current) return;

    // Use ref for cache check to avoid dependency on state
    if (now - cacheTimestampRef.current < CACHE_DURATION && subscriptionRef.current.subscribed !== undefined) {
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      isCheckingRef.current = true;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase.functions.invoke('check-subscription');
        
        if (error) {
          failureCountRef.current++;
          lastFailureTimeRef.current = Date.now();
          throw error;
        }
        
        updateSubscription(data);
        cacheTimestampRef.current = Date.now();
        failureCountRef.current = 0;
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        isCheckingRef.current = false;
        setIsLoading(false);
      }
    }, DEBOUNCE_DELAY);
  }, [updateSubscription]);

  const createCheckout = async (plan: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan }
      });
      
      if (error) throw error;
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erreur lors de la création du paiement');
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;
      
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Erreur lors de l\'ouverture du portail client');
    }
  };

  const clearCache = useCallback(() => {
    cacheTimestampRef.current = 0;
  }, []);

  // Check subscription on mount ONCE and on auth state changes
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Load from club_members first (fast)
      const { data: member } = await supabase
        .from('club_members')
        .select('subscription_type')
        .eq('user_id', user.id)
        .maybeSingle();

      if (member?.subscription_type) {
        updateSubscription({
          subscribed: ['premium', 'enterprise', 'basic'].includes(member.subscription_type),
          subscription_tier: member.subscription_type
        });
        cacheTimestampRef.current = Date.now();
        setIsLoading(false);
        return;
      }

      // Fallback for admins
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (userRole && ['admin', 'super_admin'].includes(userRole.role)) {
        updateSubscription({
          subscribed: true,
          subscription_tier: 'enterprise'
        });
        cacheTimestampRef.current = Date.now();
        setIsLoading(false);
        return;
      }

      // Last resort: edge function
      const isAutoCheckEnabled = localStorage.getItem('subscription_auto_check') !== 'false';
      if (isAutoCheckEnabled) {
        checkSubscription();
      } else {
        setIsLoading(false);
      }
    };

    checkAuth();
    
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // Reset init flag to allow re-check on sign in
          cacheTimestampRef.current = 0;
          checkAuth();
        } else if (event === 'SIGNED_OUT') {
          updateSubscription({ subscribed: false });
          setIsLoading(false);
          cacheTimestampRef.current = 0;
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
        }
      }
    );

    return () => {
      authSubscription.unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [checkSubscription, updateSubscription]);

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      isLoading,
      checkSubscription,
      createCheckout,
      openCustomerPortal,
      clearCache
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
